import { useHousehold, useMeClient } from "@common/queries";
import { ProductPhase, ProductTopic, TaskTemplateId } from "./types";
import {
  gridMargins,
  PLAYBOOK_DOMAINS,
  PlaybookDomain,
  TASK_SORT_ORDER,
  TaskStates,
} from "./constants";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useStore from "./state";
import { useTasks, useTasksOpenCount } from "./queries";
import { map, orderBy, groupBy } from "lodash";
import { useCurrentProductUpdate } from "@modules/products/mutations";
import { useMutateTaskState } from "./mutations";
import { useDebounce } from "usehooks-ts";
import { TextInput } from "react-native";
import { useForm } from "react-hook-form";
import {
  ClientIntakeData,
  ClientIntakeFormInputs,
} from "@modules/client/types";
import { useIntakeFormValues } from "@modules/client/hooks";
import { ajvResolver } from "@hookform/resolvers/ajv";
import { fullFormats } from "ajv-formats/dist/formats";
import {
  clientSchema,
  clientIntakeSchema,
  soloClientIntakeSchema,
} from "@modules/client/constants";
import { getTaskCategory, getTaskDueDate, getTaskStatus } from "./utils";
import moment from "moment";

const useGridLayouts = () => {
  const getLayout = useCallback(
    (
      tasks: Forme.EnrichedTask[] | undefined,
      taskHeightById?: Record<string, number>
    ) => {
      const rowHeight = 1;
      let heightUsedSingleCol = 0;

      const defaultLayoutProps = {
        isBounded: true,
        isDraggable: false,
        isResizable: false,
      };

      const getRowHeight = (
        task: Forme.EnrichedTask,
        taskHeightById: Record<string, number> | undefined,
        options: { isLgBreakpoint: boolean }
      ) => {
        const minItemHeight = options.isLgBreakpoint ? 100 : 90;
        const marginHeight = options.isLgBreakpoint
          ? gridMargins.sm[1]
          : gridMargins.xxs[1];
        // h = (pixelHeight + marginH) / (marginH + rowHeight)
        return taskHeightById && taskHeightById[task.id] > minItemHeight
          ? (taskHeightById?.[task.id] + marginHeight) /
              (marginHeight + rowHeight)
          : (minItemHeight + marginHeight) / (marginHeight + rowHeight);
      };

      return {
        lg: map(tasks, function (task) {
          const h = getRowHeight(task, taskHeightById, {
            isLgBreakpoint: true,
          });
          const y = heightUsedSingleCol;

          heightUsedSingleCol = heightUsedSingleCol + h;

          return {
            x: 0,
            y,
            h,
            w: 2,
            i: task.id,
            ...defaultLayoutProps,
          };
        }),
        xs: map(tasks, function (task) {
          const h = getRowHeight(task, taskHeightById, {
            isLgBreakpoint: false,
          });
          const y = heightUsedSingleCol;

          heightUsedSingleCol = heightUsedSingleCol + h;

          return {
            x: 0,
            y,
            h,
            w: 1,
            i: task.id,
            ...defaultLayoutProps,
          };
        }),
      };
    },
    []
  );

  return { getLayout };
};

const useSignAgreementTasks = () => {
  const { visibleTasks } = useTasksList();

  const signAgreementTask = visibleTasks?.find(
    (task) =>
      task.templateId === TaskTemplateId.SIGN_WEALTH_MANAGEMENT_AGREEMENT ||
      task.templateId === TaskTemplateId.SIGN_WEALTH_DIAGNOSTIC_AGREEMENT
  );
  const coClientSignAgreementTask = visibleTasks?.find(
    (task) =>
      task.templateId ===
        TaskTemplateId.SIGN_WEALTH_MANAGEMENT_AGREEMENT_COCLIENT ||
      task.templateId ===
        TaskTemplateId.SIGN_WEALTH_DIAGNOSTIC_AGREEMENT_COCLIENT
  );

  const clientIntakeTask = visibleTasks?.find(
    (task) => task.templateId === TaskTemplateId.UPDATE_PERSONAL_INFORMATION
  );

  return { signAgreementTask, coClientSignAgreementTask, clientIntakeTask };
};
const useDisabledCallToActionTooltipsByTaskTemplateId = () => {
  const { signAgreementTask, coClientSignAgreementTask, clientIntakeTask } =
    useSignAgreementTasks();

  return useMemo(() => {
    let tooltip;
    const personalInformationTooltip =
      'Complete the task "Update personal information" in order to sign this agreement.';
    const wealthManagementAgreementTooltip =
      'Complete the other "Sign Wealth Management Agreement" task in order to sign this agreement.';
    const wealthDiagnosticAgreementTooltip =
      'Complete the other "Sign Wealth Diagnostic Agreement" task in order to sign this agreement.';

    if (clientIntakeTask) {
      if (signAgreementTask)
        tooltip = {
          [signAgreementTask?.templateId as string]: personalInformationTooltip,
        };
      if (coClientSignAgreementTask)
        tooltip = {
          ...tooltip,
          [coClientSignAgreementTask.templateId as string]:
            personalInformationTooltip,
        };
    }

    if (signAgreementTask) {
      if (coClientSignAgreementTask) {
        if (
          coClientSignAgreementTask.templateId ===
          TaskTemplateId.SIGN_WEALTH_MANAGEMENT_AGREEMENT_COCLIENT
        ) {
          tooltip = {
            ...tooltip,
            [coClientSignAgreementTask.templateId as string]:
              wealthManagementAgreementTooltip,
          };
        }
        if (
          coClientSignAgreementTask.templateId ===
          TaskTemplateId.SIGN_WEALTH_DIAGNOSTIC_AGREEMENT_COCLIENT
        ) {
          tooltip = {
            ...tooltip,
            [coClientSignAgreementTask.templateId as string]:
              wealthDiagnosticAgreementTooltip,
          };
        }
      }
    }

    return tooltip;
  }, [clientIntakeTask, coClientSignAgreementTask, signAgreementTask]);
};

const useChangeTaskStatus = () => {
  const { mutateAsync: changeStatus } = useMutateTaskState();
  const { refetch: fetchTasksOpenCount } = useTasksOpenCount();

  const changeStatusToCompleted = useCallback(
    async (taskId: string, animationDuration: number) => {
      await changeStatus({
        taskId,
        state: TaskStates.Complete,
        animationDuration,
      });
      await fetchTasksOpenCount();
    },
    [changeStatus, fetchTasksOpenCount]
  );

  const changeStatusToInProgress = useCallback(
    async (taskId: string) => {
      await changeStatus({ taskId, state: TaskStates.InProgress });
      await fetchTasksOpenCount();
    },
    [changeStatus, fetchTasksOpenCount]
  );

  return { changeStatusToCompleted, changeStatusToInProgress };
};

// TODO: Rename once we got rid of Miguel's code
const useTasksList = () => {
  const {
    data: taskListBase,
    refetch: refetchTaskList,
    isLoading: isLoadingTaskList,
  } = useTasks();

  const taskList = useMemo(() => {
    return taskListBase?.map((task) => ({
      ...task,
      taskState: task?.taskStates?.at(-1)?.state as TaskStates | undefined,
      status: getTaskStatus(task),
      createdAt: new Date(task.createdAt),
      dueDate: task.dueDate ? getTaskDueDate(task.dueDate) : null,
      category: getTaskCategory(task),
    }));
  }, [taskListBase]);

  const { refetch: refetchTasksOpenCount } = useTasksOpenCount();
  const { data: household, isLoading: isLoadingHousehold } = useHousehold();

  const unorderedTasks = useMemo(() => {
    return taskList?.filter(
      (task) =>
        task.active &&
        task.taskStates?.length > 0 &&
        task.taskStates.at(-1)?.state !== TaskStates.Complete
    );
  }, [taskList]);

  const openTasks = useMemo(
    () =>
      unorderedTasks?.length
        ? orderBy(
            unorderedTasks,
            [
              (task) => {
                const dueDate = task.dueDate;
                if (!dueDate) return 999; // Tasks without due date go last

                const now = moment();
                const daysUntilDue = moment(dueDate).diff(now, "days");

                if (daysUntilDue < 0) return -1; // Expired tasks first
                return daysUntilDue;
              },
              (task) => {
                const categoryIndex = TASK_SORT_ORDER.indexOf(task.category);
                return categoryIndex === -1 ? 999 : categoryIndex;
              },
            ],
            ["asc", "asc"]
          )
        : [],
    [unorderedTasks]
  );

  const unorderedCompletedTasks = useMemo(() => {
    return taskList?.filter(
      (task) =>
        task.active &&
        task.taskStates?.length > 0 &&
        task.taskStates.at(-1)?.state === TaskStates.Complete
    );
  }, [taskList]);

  const completedTasks = useMemo(
    () =>
      unorderedCompletedTasks?.length
        ? orderBy(
            unorderedCompletedTasks,
            ["createdAt", "name"],
            ["desc", "asc"]
          )
        : [],
    [unorderedCompletedTasks]
  );

  const visibleTasks = useMemo(() => {
    const tasksByPhase = groupBy(openTasks, "productPhase");
    if (household?.currentProduct?.currentPhase) {
      return tasksByPhase[household.currentProduct.currentPhase];
    } else {
      return openTasks;
    }
  }, [household?.currentProduct?.currentPhase, openTasks]);

  return {
    openTasks,
    completedTasks,
    refetchTaskList,
    refetchTasksOpenCount,
    isLoading: isLoadingTaskList || isLoadingHousehold,
    visibleTasks,
  };
};

const usePizzaTrackerStateMachine = () => {
  const { visibleTasks, openTasks } = useTasksList();
  const { data: household } = useHousehold();
  const { mutateAsync: changeCurrentPhase } = useCurrentProductUpdate();

  const openTasksCount = useMemo(() => {
    return visibleTasks?.length || 0;
  }, [visibleTasks]);

  const getNewPhase = useCallback(
    (currentPhase: ProductPhase) => {
      const tasksByPhase = groupBy(openTasks, "productPhase");
      const tasksPhase1Count = tasksByPhase[ProductPhase.PHASE_1]?.length || 0;
      switch (currentPhase) {
        case ProductPhase.PHASE_1:
          if (openTasksCount === 0) return ProductPhase.PHASE_2;
          break;
        case ProductPhase.PHASE_2:
          if (tasksPhase1Count > 0) return ProductPhase.PHASE_1;
          break;
        default:
          return currentPhase;
      }

      return currentPhase;
    },
    [openTasks, openTasksCount]
  );

  const changeProductCurrentPhase = useCallback(async () => {
    const newPhase = getNewPhase(
      household?.currentProduct?.currentPhase as ProductPhase
    );
    if (newPhase !== household?.currentProduct?.currentPhase) {
      await changeCurrentPhase({ productPhase: newPhase });
    }
  }, [
    changeCurrentPhase,
    getNewPhase,
    household?.currentProduct?.currentPhase,
  ]);

  return { changeProductCurrentPhase };
};

const useAttachedNote = (
  attachedNote: string | null | undefined,
  onEditTaskAttachedNote: (attachedNote: string | null) => void
) => {
  type combinedRefType = HTMLTextAreaElement & TextInput;

  const [isAttachedNoteOpen, setIsAttachedNoteOpen] = useState(!!attachedNote);
  const [isAttachedNoteDirty, setIsAttachedNoteDirty] = useState(false);
  const attachedNoteRef = useRef<combinedRefType>(null);
  const onClickAttachedNote = useCallback(() => {
    setIsAttachedNoteOpen(!isAttachedNoteOpen);
    setTimeout(() => {
      attachedNoteRef.current?.focus();
    }, 0);
  }, [isAttachedNoteOpen, attachedNoteRef]);
  const [attachedNoteContent, setAttachedNoteContent] = useState(
    attachedNote || ""
  );
  useEffect(() => {
    if (attachedNote !== attachedNoteContent) {
      setAttachedNoteContent(attachedNote || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedNote]);
  const debouncedAttachedNoteContent = useDebounce<string>(
    attachedNoteContent,
    1000
  );

  useEffect(() => {
    if (isAttachedNoteDirty) {
      onEditTaskAttachedNote(debouncedAttachedNoteContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAttachedNoteContent, isAttachedNoteDirty]);

  const onBlurAttachedNote = useCallback(() => {
    if (!attachedNoteContent) {
      setIsAttachedNoteOpen(false);
    }
  }, [attachedNoteContent, setIsAttachedNoteOpen]);

  const onChangeAttachedNote = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setIsAttachedNoteDirty(true);
      setAttachedNoteContent(e.target.value);
    },
    []
  );

  const onChangeAttachedNoteMobile = useCallback((e: string) => {
    setIsAttachedNoteDirty(true);
    setAttachedNoteContent(e);
  }, []);

  return {
    isAttachedNoteOpen,
    isAttachedNoteDirty,
    attachedNoteRef,
    onClickAttachedNote,
    attachedNoteContent,
    onBlurAttachedNote,
    onChangeAttachedNote,
    setIsAttachedNoteOpen,
    setAttachedNoteContent,
    onChangeAttachedNoteMobile,
  };
};

const useIntakeForm = (
  currentPage: number,
  setCurrentPage: (page: number) => void
) => {
  const [hasCoClient, setHasCoClient] = useState(false);
  const [serverError, setServerError] = useState(false);

  const {
    values: intakeFormValues,
    isLoading,
    coClientExists,
  } = useIntakeFormValues();

  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    getValues,
    watch,
    trigger,
    control,
    formState: { isSubmitting, errors, isValid },
  } = useForm<ClientIntakeFormInputs>({
    values: intakeFormValues,
    resetOptions: {
      keepDirtyValues: true,
    },
    resolver: ajvResolver(
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-ignore */
      hasCoClient ? clientIntakeSchema : soloClientIntakeSchema,
      {
        formats: fullFormats,
        schemas: [clientSchema],
      }
    ),
    mode: "onTouched",
    criteriaMode: "firstError",
    reValidateMode: "onBlur",
  });

  // NOTE: there is probably a simpler way to keep the coClient (if exists) address information
  // in sync with the client's.
  const [
    clientAddressStreet,
    clientAddressStreet2,
    clientAddressCity,
    clientAddressState,
    clientAddressZip,
    clientMailingAddressStreet,
    clientMailingAddressStreet2,
    clientMailingAddressCity,
    clientMailingAddressState,
    clientMailingAddressZip,
  ] = watch([
    "client.ADDRESS_STREET",
    "client.ADDRESS_STREET_2",
    "client.ADDRESS_CITY",
    "client.ADDRESS_STATE",
    "client.ADDRESS_ZIP",
    "client.MAILING_ADDRESS_STREET",
    "client.MAILING_ADDRESS_STREET_2",
    "client.MAILING_ADDRESS_CITY",
    "client.MAILING_ADDRESS_STATE",
    "client.MAILING_ADDRESS_ZIP",
  ]);

  const onAddCoClient = async () => {
    const result = await trigger("client", { shouldFocus: true });
    if (result) {
      setHasCoClient(true);
      setCurrentPage(2);
    }
  };

  useEffect(() => {
    if (coClientExists) {
      setHasCoClient(true);
    }
  }, [coClientExists, setHasCoClient]);

  useEffect(() => {
    if (hasCoClient) {
      setValue("coClient", {
        ...intakeFormValues?.coClient,
        ...getValues("coClient"),
        ADDRESS_STREET: clientAddressStreet,
        ADDRESS_STREET_2: clientAddressStreet2,
        ADDRESS_CITY: clientAddressCity,
        ADDRESS_STATE: clientAddressState,
        ADDRESS_ZIP: clientAddressZip,
        MAILING_ADDRESS_STREET: clientMailingAddressStreet,
        MAILING_ADDRESS_STREET_2: clientMailingAddressStreet2,
        MAILING_ADDRESS_CITY: clientMailingAddressCity,
        MAILING_ADDRESS_STATE: clientMailingAddressState,
        MAILING_ADDRESS_ZIP: clientMailingAddressZip,
      } as ClientIntakeData);
    }
  }, [
    hasCoClient,
    intakeFormValues,
    setValue,
    getValues,
    clientAddressStreet,
    clientAddressStreet2,
    clientAddressCity,
    clientAddressState,
    clientAddressZip,
    clientMailingAddressStreet,
    clientMailingAddressStreet2,
    clientMailingAddressCity,
    clientMailingAddressState,
    clientMailingAddressZip,
  ]);

  const isSubmitDisabled = isSubmitting || !isValid;

  const [showPage1, setShowPage1] = useState(currentPage === 1);
  const [showPage2, setShowPage2] = useState(currentPage === 2);

  useEffect(() => {
    if (hasCoClient && currentPage === 2) {
      setShowPage1(false);
      setShowPage2(true);
    } else if (currentPage === 1) {
      setShowPage2(false);
      setShowPage1(true);
    }
  }, [setShowPage1, setShowPage2, hasCoClient, currentPage]);

  useEffect(() => {
    if (showPage2) {
      setTimeout(() => {
        setFocus("coClient.FIRST_NAME");
      }, 100);
    } else if (showPage1) {
      setTimeout(() => {
        setFocus("client.ADDRESS_STREET");
      }, 100);
    }
  }, [showPage1, showPage2, setFocus]);

  return {
    hasCoClient,
    setHasCoClient,
    serverError,
    setServerError,
    register,
    handleSubmit,
    control,
    isSubmitting,
    errors,
    isLoading,
    intakeFormValues,
    isSubmitDisabled,
    showPage1,
    showPage2,
    onAddCoClient,
  };
};

const useProgressByPlaybookDomain = (
  openTasks: Forme.Task[],
  completedTasks: Forme.Task[]
) =>
  useMemo(() => {
    const progressByPlaybookDomain = PLAYBOOK_DOMAINS.reduce(
      (memo, playbookDomain) => {
        memo[playbookDomain] = 0;
        return memo;
      },
      {} as Record<PlaybookDomain, number>
    );

    const taskCountsByPlaybookDomain = PLAYBOOK_DOMAINS.reduce(
      (memo, playbookDomain) => {
        memo[playbookDomain] = { openCount: 0, totalCount: 0 };
        return memo;
      },
      {} as Record<PlaybookDomain, { openCount: number; totalCount: number }>
    );

    for (const task of completedTasks) {
      if (PLAYBOOK_DOMAINS.includes(task.productTopic)) {
        // ProductTopic task
        taskCountsByPlaybookDomain[
          task.productTopic as ProductTopic
        ].totalCount += 1;
      } else if (task.productPhase === ProductPhase.PHASE_1) {
        // PHASE_1 task
        taskCountsByPlaybookDomain[
          task.productPhase as ProductPhase.PHASE_1
        ].totalCount += 1;
      }
    }
    for (const task of openTasks) {
      if (PLAYBOOK_DOMAINS.includes(task.productTopic)) {
        // ProductTopic task
        taskCountsByPlaybookDomain[
          task.productTopic as ProductTopic
        ].openCount += 1;
        taskCountsByPlaybookDomain[
          task.productTopic as ProductTopic
        ].totalCount += 1;
      } else if (task.productPhase === ProductPhase.PHASE_1) {
        // PHASE_1 task
        taskCountsByPlaybookDomain[
          task.productPhase as ProductPhase.PHASE_1
        ].openCount += 1;
        taskCountsByPlaybookDomain[
          task.productPhase as ProductPhase.PHASE_1
        ].totalCount += 1;
      }
    }
    // Calculation:
    // If Zero Tasks, Progress = 0
    // If < 1 CompletedTask, Progress = 5
    // If >= 1 Completed Task, Progress = (Completed Task Count) / (Total Task Count)
    const MINIMUM_PROGRESS = 10;
    for (const _playbookDomain in taskCountsByPlaybookDomain) {
      const playbookDomain = _playbookDomain as PlaybookDomain;
      const { openCount, totalCount } =
        taskCountsByPlaybookDomain[playbookDomain];
      if (totalCount === 0) {
        // No Tasks
        progressByPlaybookDomain[playbookDomain] = 0;
      } else if (openCount === 0) {
        // 100% Completed Tasks
        progressByPlaybookDomain[playbookDomain] = 100;
      } else {
        progressByPlaybookDomain[playbookDomain] = Math.max(
          100 * ((totalCount - openCount) / totalCount),
          MINIMUM_PROGRESS
        );
      }
    }
    return progressByPlaybookDomain;
  }, [completedTasks, openTasks]);

// TODO: the logic to sort tasks is only being applied here for completed tasks.
// We should centralize sorting of tasks. This function is confusing as-is.
const useOrderedOpenTasks = (
  tasks: Forme.EnrichedTask[],
  isCompleted: boolean
) =>
  useMemo(
    () =>
      isCompleted
        ? orderBy(tasks, (task) => task.taskStates.at(-1)?.createdAt, ["desc"])
        : tasks,
    [tasks, isCompleted]
  );

export {
  useGridLayouts,
  useChangeTaskStatus,
  useTasksList,
  usePizzaTrackerStateMachine,
  useAttachedNote,
  useIntakeForm,
  useDisabledCallToActionTooltipsByTaskTemplateId,
  useSignAgreementTasks,
  useProgressByPlaybookDomain,
  useOrderedOpenTasks,
};
